// FILE: /src/Components/Apps/BackgroundRemoverApp.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Empty,
  Input,
  Modal,
  Spin,
  Tag,
  Tooltip,
  message,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  HistoryOutlined,
  LinkOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  ScissorOutlined,
} from "@ant-design/icons";
import { fetchAssets } from "../../store/Slices/librarySlice";
import { fetchAppsCatalog, runBackgroundRemover } from "../../services/apps";

const SOURCE_TYPES = {
  UPLOAD: "upload",
  LIBRARY: "library",
  URL: "url",
};

const LIBRARY_PAGE_SIZE = 16;
const PROCESS_BATCH_SIZE = 10;

const isImageAsset = (asset) => {
  const name = String(asset?.name || "").toLowerCase();
  const url = String(asset?.url || "").toLowerCase();

  return (
    /\.(png|jpg|jpeg|webp|gif)$/i.test(name) ||
    /\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(url)
  );
};

const chunkArray = (items = [], size = PROCESS_BATCH_SIZE) => {
  const chunks = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
};

const createQueueItem = ({
  source,
  file = null,
  imageUrl = null,
  previewUrl,
  name,
}) => ({
  id: `${source}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  source,
  file,
  imageUrl,
  previewUrl,
  name,
  status: "idle",
  result: null,
  transaction: null,
  error: null,
});

const SourceButton = ({ active, icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold border transition-all ${
      active
        ? "bg-purple-50 text-purple-700 border-purple-200 shadow-sm"
        : "bg-white text-gray-500 border-gray-200 hover:border-purple-200 hover:text-purple-700 hover:bg-purple-50/50"
    }`}
  >
    {icon}
    {label}
  </button>
);

const StatusBadge = ({ status }) => {
  const map = {
    idle: {
      color: "default",
      label: "Ready",
    },
    processing: {
      color: "blue",
      label: "Processing",
    },
    completed: {
      color: "green",
      label: "Done",
    },
    failed: {
      color: "red",
      label: "Failed",
    },
  };

  const config = map[status] || map.idle;

  return (
    <Tag color={config.color} className="m-0 rounded-full text-[11px]">
      {config.label}
    </Tag>
  );
};

const getSourceLabel = (source) => {
  if (source === SOURCE_TYPES.UPLOAD) return "Upload";
  if (source === SOURCE_TYPES.LIBRARY) return "Library";
  if (source === SOURCE_TYPES.URL) return "URL";
  return "Image";
};

const QueueImageCard = ({
  item,
  requiredCredits,
  onProcess,
  onRemove,
  isProcessingAll,
  canProcess,
}) => {
  const outputUrl = item.result?.outputAssetUrl || null;
  const displayUrl = outputUrl || item.previewUrl;
  const isProcessing = item.status === "processing";

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm flex flex-col h-[232px]">
      <div className="relative h-[122px] bg-gray-100 flex-shrink-0 overflow-hidden">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={item.name || "Image"}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <i className="fa-regular fa-image text-2xl"></i>
          </div>
        )}

        {isProcessing && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center">
            <Spin size="small" />
            <p className="text-[11px] font-semibold text-gray-600 mt-2 mb-0">
              Removing...
            </p>
          </div>
        )}

        {outputUrl && (
          <div className="absolute top-2 left-2">
            <Tag color="green" className="m-0 rounded-full text-[11px]">
              Result
            </Tag>
          </div>
        )}

        <button
          type="button"
          onClick={() => onRemove(item.id)}
          disabled={isProcessing}
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border border-red-100 text-red-500 hover:bg-red-50 disabled:opacity-50 flex items-center justify-center shadow-sm"
        >
          <DeleteOutlined className="text-xs" />
        </button>
      </div>

      <div className="p-3 flex flex-col flex-1 min-h-0">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <p
              className="text-sm font-bold text-gray-900 truncate mb-0"
              title={item.name}
            >
              {item.name || "Image"}
            </p>
            <p className="text-xs text-gray-500 mb-0">
              {getSourceLabel(item.source)}
            </p>
          </div>

          <StatusBadge status={item.status} />
        </div>

        {item.error && (
          <Tooltip title={item.error}>
            <p className="text-xs text-red-600 line-clamp-1 mb-2">
              {item.error}
            </p>
          </Tooltip>
        )}

        <div className="mt-auto flex items-center gap-2">
          {outputUrl ? (
            <>
              <Button
                size="small"
                href={outputUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                Open
              </Button>

              <Button
                size="small"
                type="primary"
                href={outputUrl}
                target="_blank"
                download
                className="flex-1"
              >
                Download
              </Button>
            </>
          ) : (
            <Button
              size="small"
              type="primary"
              icon={<ScissorOutlined />}
              loading={isProcessing}
              disabled={!canProcess || isProcessingAll}
              onClick={() => onProcess(item.id)}
              className="w-full"
            >
              Remove · {requiredCredits} Credit
              {requiredCredits === 1 ? "" : "s"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const BackgroundRemoverApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const latestQueueRef = useRef([]);

  const { selectedGroup, groups } = useSelector((state) => state.groups);
  const { assets, status: libraryStatus } = useSelector((state) => state.library);

  const selectedGroupId = useMemo(() => {
    return groups.find((group) => group.name === selectedGroup)?._id;
  }, [groups, selectedGroup]);

  const [catalogLoading, setCatalogLoading] = useState(true);
  const [credits, setCredits] = useState(null);
  const [appConfig, setAppConfig] = useState(null);

  const [sourceType, setSourceType] = useState(SOURCE_TYPES.UPLOAD);
  const [queueItems, setQueueItems] = useState([]);
  const [manualImageUrl, setManualImageUrl] = useState("");
  const [libraryPage, setLibraryPage] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessingAll, setIsProcessingAll] = useState(false);

  useEffect(() => {
    latestQueueRef.current = queueItems;
  }, [queueItems]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);

    try {
      const response = await fetchAppsCatalog();
      const backgroundApp = (response.apps || []).find(
        (app) => app.appId === "background.remove"
      );

      setAppConfig(backgroundApp || null);
      setCredits(response.credits || null);
    } catch (error) {
      message.error(error.message || "Failed to load Background Remover.");
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const refreshCredits = useCallback(async () => {
    try {
      const response = await fetchAppsCatalog();
      setCredits(response.credits || null);
    } catch (error) {
      console.warn("Failed to refresh app credits:", error.message);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (selectedGroupId) {
      dispatch(fetchAssets(selectedGroupId));
    }
  }, [selectedGroupId, dispatch]);

  useEffect(() => {
    return () => {
      latestQueueRef.current.forEach((item) => {
        if (item.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, []);

  const imageAssets = useMemo(() => {
    return (assets || []).filter(isImageAsset);
  }, [assets]);

  const totalLibraryPages = Math.max(
    Math.ceil(imageAssets.length / LIBRARY_PAGE_SIZE),
    1
  );

  const visibleLibraryAssets = useMemo(() => {
    const start = (libraryPage - 1) * LIBRARY_PAGE_SIZE;
    return imageAssets.slice(start, start + LIBRARY_PAGE_SIZE);
  }, [imageAssets, libraryPage]);

  useEffect(() => {
    if (libraryPage > totalLibraryPages) {
      setLibraryPage(totalLibraryPages);
    }
  }, [libraryPage, totalLibraryPages]);

  const appCost = appConfig?.cost || { credits: 0 };
  const requiredCredits = Number(appCost?.credits || 0);
  const remainingCredits = Number(credits?.remainingCredits || 0);

  const processableItems = useMemo(() => {
    return queueItems.filter(
      (item) => item.status === "idle" || item.status === "failed"
    );
  }, [queueItems]);

  const totalCostForAll = processableItems.length * requiredCredits;
  const hasEnoughForAll = remainingCredits >= totalCostForAll;

  const refreshLibrary = useCallback(() => {
    if (selectedGroupId) {
      dispatch(fetchAssets(selectedGroupId));
    }
  }, [dispatch, selectedGroupId]);

  const updateQueueItem = useCallback((itemId, patch) => {
    setQueueItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item))
    );
  }, []);

  const addFilesToQueue = useCallback((fileList) => {
    const files = Array.from(fileList || []);

    if (!files.length) return;

    const imageFiles = files.filter((file) => file.type?.startsWith("image/"));

    if (imageFiles.length !== files.length) {
      message.warning("Only image files were added. Unsupported files were skipped.");
    }

    const newItems = imageFiles.map((file) => {
      const previewUrl = URL.createObjectURL(file);

      return createQueueItem({
        source: SOURCE_TYPES.UPLOAD,
        file,
        previewUrl,
        name: file.name,
      });
    });

    setQueueItems((prev) => [...newItems, ...prev]);
    setSourceType(SOURCE_TYPES.UPLOAD);
  }, []);

  const handleFileSelect = (event) => {
    addFilesToQueue(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    addFilesToQueue(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleSelectLibraryAsset = (asset) => {
    const alreadyAdded = queueItems.some(
      (item) => item.source === SOURCE_TYPES.LIBRARY && item.imageUrl === asset.url
    );

    if (alreadyAdded) {
      setQueueItems((prev) =>
        prev.filter(
          (item) =>
            !(item.source === SOURCE_TYPES.LIBRARY && item.imageUrl === asset.url)
        )
      );
      return;
    }

    const item = createQueueItem({
      source: SOURCE_TYPES.LIBRARY,
      imageUrl: asset.url,
      previewUrl: asset.url,
      name: asset.name || "Library image",
    });

    setQueueItems((prev) => [item, ...prev]);
  };

  const handleAddUrl = () => {
    const url = manualImageUrl.trim();

    if (!url) {
      message.warning("Please enter an image URL.");
      return;
    }

    const alreadyAdded = queueItems.some(
      (item) => item.source === SOURCE_TYPES.URL && item.imageUrl === url
    );

    if (alreadyAdded) {
      message.info("This URL is already added.");
      return;
    }

    const item = createQueueItem({
      source: SOURCE_TYPES.URL,
      imageUrl: url,
      previewUrl: url,
      name: "URL image",
    });

    setQueueItems((prev) => [item, ...prev]);
    setManualImageUrl("");
  };

  const handleRemoveItem = (itemId) => {
    setQueueItems((prev) => {
      const itemToRemove = prev.find((item) => item.id === itemId);

      if (itemToRemove?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(itemToRemove.previewUrl);
      }

      return prev.filter((item) => item.id !== itemId);
    });
  };

  const handleClearQueue = () => {
    queueItems.forEach((item) => {
      if (item.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });

    setQueueItems([]);
  };

  const processItem = async (item, options = {}) => {
    const { skipLocalCreditCheck = false, refreshAfterComplete = true } = options;

    if (!selectedGroupId) {
      message.error("Please select a brand first.");
      return {
        success: false,
        itemId: item.id,
        error: "Please select a brand first.",
      };
    }

    if (!skipLocalCreditCheck && remainingCredits < requiredCredits) {
      Modal.confirm({
        title: "Not enough credits",
        content: `This action needs ${requiredCredits} credit${
          requiredCredits === 1 ? "" : "s"
        }. You currently have ${remainingCredits}.`,
        okText: "Buy Add-ons",
        onOk: () => navigate("/Dashboard/AccountSetting"),
      });

      return {
        success: false,
        itemId: item.id,
        error: "Not enough credits.",
      };
    }

    updateQueueItem(item.id, {
      status: "processing",
      error: null,
    });

    try {
      const response = await runBackgroundRemover({
        groupId: selectedGroupId,
        file: item.file || null,
        imageUrl: item.file ? null : item.imageUrl,
      });

      updateQueueItem(item.id, {
        status: "completed",
        result: response.result || null,
        transaction: response.transaction || null,
        error: null,
      });

      if (response.credits) {
        setCredits(response.credits);
      }

      if (refreshAfterComplete) {
        refreshLibrary();
      }

      return {
        success: true,
        itemId: item.id,
        response,
      };
    } catch (error) {
      updateQueueItem(item.id, {
        status: "failed",
        error: error.message || "Background removal failed.",
      });

      if (error.isLimitError) {
        Modal.confirm({
          title: "Not enough credits",
          content: error.message,
          okText: "Buy Add-ons",
          onOk: () => navigate("/Dashboard/AccountSetting"),
        });
      } else if (!isProcessingAll) {
        message.error(error.message || "Background removal failed.");
      }

      return {
        success: false,
        itemId: item.id,
        error: error.message || "Background removal failed.",
      };
    }
  };

  const handleProcessOne = async (itemId) => {
    const item = queueItems.find((queueItem) => queueItem.id === itemId);

    if (!item || item.status === "processing") return;

    await processItem(item, {
      skipLocalCreditCheck: false,
      refreshAfterComplete: true,
    });

    await refreshCredits();
  };

  const handleProcessAll = async () => {
    if (!processableItems.length) {
      message.info("No images are ready to process.");
      return;
    }

    if (!hasEnoughForAll) {
      Modal.confirm({
        title: "Not enough credits",
        content: `Removing backgrounds for ${processableItems.length} image${
          processableItems.length === 1 ? "" : "s"
        } needs ${totalCostForAll} credits, but you only have ${remainingCredits}.`,
        okText: "Buy Add-ons",
        onOk: () => navigate("/Dashboard/AccountSetting"),
      });
      return;
    }

    setIsProcessingAll(true);

    const hideLoading = message.loading({
      content: `Processing ${processableItems.length} image${
        processableItems.length === 1 ? "" : "s"
      } in batches of ${PROCESS_BATCH_SIZE}...`,
      duration: 0,
      key: "background-remove-all",
    });

    try {
      const batches = chunkArray(processableItems, PROCESS_BATCH_SIZE);
      const results = [];

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
        const batch = batches[batchIndex];

        message.loading({
          content: `Processing batch ${batchIndex + 1} of ${batches.length} · ${
            batch.length
          } image${batch.length === 1 ? "" : "s"}...`,
          duration: 0,
          key: "background-remove-all",
        });

        // Process 10 items concurrently, then move to next 10.
        // Backend still deducts credits per item and saves each result independently.
        // eslint-disable-next-line no-await-in-loop
        const batchResults = await Promise.all(
          batch.map((item) =>
            processItem(item, {
              skipLocalCreditCheck: true,
              refreshAfterComplete: false,
            })
          )
        );

        results.push(...batchResults);
      }

      const successCount = results.filter((result) => result.success).length;
      const failedCount = results.length - successCount;

      refreshLibrary();
      await refreshCredits();

      if (failedCount > 0 && successCount > 0) {
        message.warning({
          content: `${successCount} image${
            successCount === 1 ? "" : "s"
          } completed, ${failedCount} failed.`,
          key: "background-remove-all",
          duration: 5,
        });
      } else if (failedCount > 0) {
        message.error({
          content: `${failedCount} image${
            failedCount === 1 ? "" : "s"
          } failed. Please review the failed items.`,
          key: "background-remove-all",
          duration: 5,
        });
      } else {
        message.success({
          content: `Background removed for ${successCount} image${
            successCount === 1 ? "" : "s"
          }.`,
          key: "background-remove-all",
          duration: 4,
        });
      }
    } finally {
      hideLoading();
      setIsProcessingAll(false);
    }
  };

  const renderUploadPanel = () => (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`h-full min-h-[230px] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center text-center transition-all ${
        isDragging
          ? "border-purple-400 bg-purple-50"
          : "border-gray-300 bg-white hover:bg-purple-50/40 hover:border-purple-300"
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      <div className="w-14 h-14 rounded-full bg-gray-50 border border-gray-200 shadow-sm flex items-center justify-center mb-3">
        <CloudUploadOutlined className="text-3xl text-gray-400" />
      </div>

      <h2 className="text-lg font-extrabold text-gray-900 mb-1">
        Drop images here
      </h2>

      <p className="text-xs text-gray-500 mb-4">
        PNG, JPG, JPEG, WEBP, or GIF. Multiple images supported.
      </p>

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => fileInputRef.current?.click()}
      >
        Upload Images
      </Button>
    </div>
  );

  const renderLibraryPanel = () => {
    const from =
      imageAssets.length === 0
        ? 0
        : (libraryPage - 1) * LIBRARY_PAGE_SIZE + 1;
    const to = Math.min(libraryPage * LIBRARY_PAGE_SIZE, imageAssets.length);

    return (
      <div className="h-full min-h-0 flex flex-col">
        <div className="flex items-center justify-between gap-3 mb-3 flex-shrink-0">
          <div>
            <h2 className="text-sm font-bold text-gray-900 mb-0">
              Media Library
            </h2>
            <p className="text-xs text-gray-500 mb-0">
              Showing images only. Select one or multiple.
            </p>
          </div>

          <Button size="small" icon={<ReloadOutlined />} onClick={refreshLibrary}>
            Refresh
          </Button>
        </div>

        {libraryStatus === "loading" ? (
          <div className="flex justify-center items-center flex-1">
            <Spin />
          </div>
        ) : imageAssets.length === 0 ? (
          <div className="bg-white border border-dashed rounded-2xl flex-1 flex items-center justify-center">
            <Empty description="No image assets found." />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2 overflow-y-auto pr-1 flex-1 min-h-0">
              {visibleLibraryAssets.map((asset) => {
                const selected = queueItems.some(
                  (item) =>
                    item.source === SOURCE_TYPES.LIBRARY &&
                    item.imageUrl === asset.url
                );

                return (
                  <button
                    key={asset.url}
                    type="button"
                    onClick={() => handleSelectLibraryAsset(asset)}
                    title={asset.name}
                    className={`relative aspect-square rounded-xl overflow-hidden border bg-gray-100 transition-all ${
                      selected
                        ? "border-purple-600 ring-2 ring-purple-200 shadow-md"
                        : "border-gray-200 hover:border-purple-300 hover:shadow-sm"
                    }`}
                  >
                    <img
                      src={asset.url}
                      alt={asset.name || "Library asset"}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />

                    {selected && (
                      <div className="absolute inset-0 bg-purple-950/35 flex items-center justify-center">
                        <i className="fa-solid fa-circle-check text-white text-xl"></i>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 flex items-center justify-between gap-2 flex-shrink-0">
              <p className="text-xs text-gray-500 mb-0">
                {from}-{to} of {imageAssets.length}
              </p>

              <div className="flex items-center gap-2">
                <Button
                  size="small"
                  disabled={libraryPage <= 1}
                  onClick={() => setLibraryPage((prev) => Math.max(prev - 1, 1))}
                >
                  Prev
                </Button>

                <span className="text-xs text-gray-500">
                  {libraryPage}/{totalLibraryPages}
                </span>

                <Button
                  size="small"
                  disabled={libraryPage >= totalLibraryPages}
                  onClick={() =>
                    setLibraryPage((prev) =>
                      Math.min(prev + 1, totalLibraryPages)
                    )
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderUrlPanel = () => (
    <div className="h-full min-h-[230px] flex items-center justify-center">
      <div className="w-full">
        <div className="text-center mb-4">
          <div className="w-12 h-12 rounded-2xl bg-white border border-gray-200 shadow-sm mx-auto flex items-center justify-center mb-3">
            <LinkOutlined className="text-xl text-purple-600" />
          </div>

          <h2 className="text-lg font-extrabold text-gray-900 mb-1">
            Paste image URL
          </h2>

          <p className="text-xs text-gray-500 mb-0">
            Public image URL only. Original and output are saved to Media Library.
          </p>
        </div>

        <div className="flex gap-2">
          <Input
            size="large"
            value={manualImageUrl}
            onChange={(event) => setManualImageUrl(event.target.value)}
            placeholder="https://example.com/image.jpg"
            className="rounded-xl"
            onPressEnter={handleAddUrl}
          />

          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleAddUrl}
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );

  if (catalogLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!appConfig) {
    return (
      <div className="h-screen bg-gray-50 p-6 overflow-hidden">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/Dashboard/Apps")}>
          Back to Apps
        </Button>

        <div className="bg-white rounded-3xl border p-12 mt-6">
          <Empty description="Background Remover app is not available." />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 px-4 py-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto h-full flex flex-col">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-3 flex-shrink-0">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/Dashboard/Apps")}
          >
            Back
          </Button>

          <Button
            type="text"
            icon={<HistoryOutlined />}
            onClick={() => navigate("/Dashboard/Apps/history")}
          >
            Activity
          </Button>
        </header>

        <section className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden flex-1 min-h-0 flex flex-col">
          <div className="px-5 py-4 sm:px-6 border-b border-gray-100 bg-gradient-to-br from-white to-purple-50/50 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-purple-50 text-purple-700 ring-1 ring-purple-100 flex items-center justify-center">
                  <ScissorOutlined className="text-xl" />
                </div>

                <div>
                  <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900 mb-0">
                    Background Remover
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500 mb-0">
                    Upload, choose from Media Library, or paste a URL. Originals and results are saved to Media Library.
                  </p>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-purple-100 px-3 py-1.5 text-xs sm:text-sm font-semibold text-purple-700 shadow-sm w-fit">
                <i className="fa-solid fa-bolt text-yellow-500"></i>
                {requiredCredits} credit{requiredCredits === 1 ? "" : "s"} / image
              </div>
            </div>
          </div>

          {!selectedGroupId && (
            <div className="px-5 pt-4 sm:px-6 flex-shrink-0">
              <Alert
                type="warning"
                showIcon
                message="No brand selected"
                description="Please select a brand from the sidebar before using Background Remover."
              />
            </div>
          )}

          <div className="p-4 sm:p-5 flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4 overflow-hidden">
            <aside className="min-h-0 flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                <SourceButton
                  active={sourceType === SOURCE_TYPES.UPLOAD}
                  icon={<CloudUploadOutlined />}
                  label="Upload"
                  onClick={() => setSourceType(SOURCE_TYPES.UPLOAD)}
                />

                <SourceButton
                  active={sourceType === SOURCE_TYPES.LIBRARY}
                  icon={<PictureOutlined />}
                  label="Library"
                  onClick={() => setSourceType(SOURCE_TYPES.LIBRARY)}
                />

                <SourceButton
                  active={sourceType === SOURCE_TYPES.URL}
                  icon={<LinkOutlined />}
                  label="URL"
                  onClick={() => setSourceType(SOURCE_TYPES.URL)}
                />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 flex-1 min-h-0 overflow-hidden">
                {sourceType === SOURCE_TYPES.UPLOAD && renderUploadPanel()}
                {sourceType === SOURCE_TYPES.LIBRARY && renderLibraryPanel()}
                {sourceType === SOURCE_TYPES.URL && renderUrlPanel()}
              </div>
            </aside>

            <main className="min-h-0 flex flex-col">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3 flex-shrink-0">
                <div>
                  <h2 className="text-base font-extrabold text-gray-900 mb-0">
                    Images
                  </h2>
                  <p className="text-xs text-gray-500 mb-0">
                    Remove backgrounds individually or process all queued images.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {queueItems.length > 0 && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleClearQueue}
                      disabled={isProcessingAll}
                    >
                      Clear
                    </Button>
                  )}

                  <Tooltip
                    title={
                      processableItems.length === 0
                        ? "No ready images to process."
                        : `Processes ${PROCESS_BATCH_SIZE} images at once. Cost: ${processableItems.length} × ${requiredCredits} = ${totalCostForAll} credits`
                    }
                  >
                    <Button
                      type="primary"
                      icon={<ScissorOutlined />}
                      loading={isProcessingAll}
                      disabled={
                        !selectedGroupId ||
                        processableItems.length === 0 ||
                        !hasEnoughForAll ||
                        isProcessingAll
                      }
                      onClick={handleProcessAll}
                    >
                      Remove All · {totalCostForAll} Credit
                      {totalCostForAll === 1 ? "" : "s"}
                    </Button>
                  </Tooltip>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 flex-1 min-h-0 overflow-hidden">
                {queueItems.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-center">
                    <Empty description="Add images from upload, Media Library, or URL to start." />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 h-full overflow-y-auto pr-1">
                    {queueItems.map((item) => {
                      const isProcessing = item.status === "processing";
                      const canProcess =
                        selectedGroupId &&
                        !isProcessing &&
                        item.status !== "completed" &&
                        remainingCredits >= requiredCredits;

                      return (
                        <QueueImageCard
                          key={item.id}
                          item={item}
                          requiredCredits={requiredCredits}
                          onProcess={handleProcessOne}
                          onRemove={handleRemoveItem}
                          isProcessingAll={isProcessingAll}
                          canProcess={canProcess}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {processableItems.length > 0 && !hasEnoughForAll && (
                <div className="mt-3 flex-shrink-0">
                  <Alert
                    type="error"
                    showIcon
                    message="Insufficient credits"
                    description={`You need ${totalCostForAll} credits to process all ready images, but only have ${remainingCredits}. You can process fewer images or buy add-ons.`}
                  />
                </div>
              )}
            </main>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BackgroundRemoverApp;